import {
  computeAdHocMesh,
  getBucketPositionsForAdHocMesh,
  hasSegmentIndexInDataStoreCached,
  sendAnalyticsEvent,
} from "admin/rest_api";
import ErrorHandling from "libs/error_handling";
import { V3 } from "libs/mjs";
import ThreeDMap from "libs/ThreeDMap";
import Toast from "libs/toast";
import { sleep } from "libs/utils";
import get from "lodash-es/get";
import set from "lodash-es/set";
import type { ActionPattern } from "redux-saga/effects";
import { actionChannel, call, put, race, take, takeEvery } from "typed-redux-saga";
import type { AdditionalCoordinate } from "types/api_types";
import type { BigIntAsKey } from "types/type_utils";
import { WkDevFlags } from "viewer/api/wk_dev";
import type { Vector3 } from "viewer/constants";
import { MappingStatusEnum } from "viewer/constants";
import { sortByDistanceTo } from "viewer/controller/mesh_helpers";
import getSceneController from "viewer/controller/scene_controller_provider";
import {
  getMagInfo,
  getMappingInfo,
  getVisibleSegmentationLayer,
} from "viewer/model/accessors/dataset_accessor";
import {
  getActiveSegmentationTracing,
  getMeshInfoForSegment,
} from "viewer/model/accessors/volumetracing_accessor";
import type { Action } from "viewer/model/actions/actions";
import {
  addAdHocMeshAction,
  finishedLoadingMeshAction,
  type RefreshMeshAction,
  type RemoveMeshAction,
  removeMeshAction,
  startedLoadingMeshAction,
} from "viewer/model/actions/annotation_actions";
import { saveNowAction } from "viewer/model/actions/save_actions";
import {
  type AdHocMeshInfo,
  type LoadAdHocMeshAction,
  loadPrecomputedMeshAction,
} from "viewer/model/actions/segmentation_actions";
import type { LayerSourceInfo } from "viewer/model/bucket_data_handling/wkstore_helper";
import type DataLayer from "viewer/model/data_layer";
import type { MagInfo } from "viewer/model/helpers/mag_info";
import { zoomedAddressToAnotherZoomStepWithInfo } from "viewer/model/helpers/position_converter";
import type { Saga } from "viewer/model/sagas/effect_generators";
import { select } from "viewer/model/sagas/effect_generators";
import { Model } from "viewer/singletons";
import Store, { type StoreDataset, type VolumeTracing } from "viewer/store";
import { getAdditionalCoordinatesAsString } from "../../accessors/flycam_accessor";
import { ensureSceneControllerInitialized, ensureWkInitialized } from "../ready_sagas";
import { acquireMeshWorker, releaseMeshWorker } from "./common_mesh_saga";

const MAX_RETRY_COUNT = 5;
const RETRY_WAIT_TIME = 5000;
const MESH_CHUNK_THROTTLE_DELAY = 500;

// The calculation of a mesh is spread across multiple requests.
// In order to avoid, that a huge amount of chunks is downloaded at full speed,
// we artificially throttle the download speed after the first MESH_CHUNK_THROTTLE_LIMIT
// requests for each segment.
const batchCounterPerSegment: Record<BigIntAsKey, number> = {};
const MESH_CHUNK_THROTTLE_LIMIT = 50;

// Maps from additional coordinates, layerName and segmentId to a ThreeDMap that stores for each chunk
// (at x, y, z) position whether the mesh chunk was loaded.
const adhocMeshesMapByLayer: Record<string, Record<string, Map<bigint, ThreeDMap<boolean>>>> = {};

function marchingCubeSizeInTargetMag(): Vector3 {
  return WkDevFlags.meshing.marchingCubeSizeInTargetMag;
}
const modifiedCells: Set<bigint> = new Set();

function getOrAddMapForSegment(
  layerName: string,
  segmentId: bigint,
  additionalCoordinates?: AdditionalCoordinate[] | null,
): ThreeDMap<boolean> {
  const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);

  const keys = [additionalCoordKey, layerName];
  // create new map if adhocMeshesMapByLayer[additionalCoordinatesString][layerName] doesn't exist yet.
  set(adhocMeshesMapByLayer, keys, get(adhocMeshesMapByLayer, keys, new Map()));
  const meshesMap = adhocMeshesMapByLayer[additionalCoordKey][layerName];
  const maybeMap = meshesMap.get(segmentId);

  if (maybeMap == null) {
    const newMap = new ThreeDMap<boolean>();
    meshesMap.set(segmentId, newMap);
    return newMap;
  }

  return maybeMap;
}

function* removeMesh(action: RemoveMeshAction, removeFromScene: boolean = true): Saga<void> {
  const additionalCoordinates = yield* select((state) => state.flycam.additionalCoordinates);
  const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
  const { layerName } = action;
  const segmentId = action.segmentId;

  if (removeFromScene) {
    getSceneController().segmentMeshController.removeMeshById(segmentId, layerName);
  }
  removeMapForSegment(layerName, segmentId, additionalCoordKey);
}

function removeMapForSegment(
  layerName: string,
  segmentId: bigint,
  additionalCoordinateKey: string,
): void {
  if (
    adhocMeshesMapByLayer[additionalCoordinateKey] == null ||
    adhocMeshesMapByLayer[additionalCoordinateKey][layerName] == null
  ) {
    return;
  }

  adhocMeshesMapByLayer[additionalCoordinateKey][layerName].delete(segmentId);
}

function getCubeSizeInMag1(zoomStep: number, magInfo: MagInfo): Vector3 {
  // Convert marchingCubeSizeInTargetMag to mag1 via zoomStep
  // Drop the last element of the Vector4;
  const [x, y, z] = zoomedAddressToAnotherZoomStepWithInfo(
    [...marchingCubeSizeInTargetMag(), zoomStep],
    magInfo,
    0,
  );
  return [x, y, z];
}

function clipPositionToCubeBoundary(
  position: Vector3,
  zoomStep: number,
  magInfo: MagInfo,
): Vector3 {
  const cubeSizeInMag1 = getCubeSizeInMag1(zoomStep, magInfo);
  const currentCube = V3.floor(V3.divide3(position, cubeSizeInMag1));
  const clippedPosition = V3.scale3(currentCube, cubeSizeInMag1);
  return clippedPosition;
}

// front_xy, front_xz, front_yz, back_xy, back_xz, back_yz
const NEIGHBOR_LOOKUP = [
  [0, 0, -1],
  [0, -1, 0],
  [-1, 0, 0],
  [0, 0, 1],
  [0, 1, 0],
  [1, 0, 0],
];

function getNeighborPosition(
  clippedPosition: Vector3,
  neighborId: number,
  zoomStep: number,
  magInfo: MagInfo,
): Vector3 {
  const neighborMultiplier = NEIGHBOR_LOOKUP[neighborId];
  const cubeSizeInMag1 = getCubeSizeInMag1(zoomStep, magInfo);
  const neighboringPosition: Vector3 = [
    clippedPosition[0] + neighborMultiplier[0] * cubeSizeInMag1[0],
    clippedPosition[1] + neighborMultiplier[1] * cubeSizeInMag1[1],
    clippedPosition[2] + neighborMultiplier[2] * cubeSizeInMag1[2],
  ];
  return neighboringPosition;
}

function* loadAdHocMeshFromAction(action: LoadAdHocMeshAction): Saga<void> {
  const { layerName } = action;
  const layer =
    layerName != null ? Model.getLayerByName(layerName) : Model.getVisibleSegmentationLayer();
  if (layer == null) {
    return;
  }
  // Remove older mesh instance if it exists already.
  yield* put(removeMeshAction(layer.name, action.segmentId));

  try {
    yield* call(
      loadAdHocMesh,
      action.seedPosition,
      action.seedAdditionalCoordinates,
      action.segmentId,
      false,
      layer.name,
      action.extraInfo,
    );
  } catch (exc) {
    Toast.error(`The mesh for segment ${action.segmentId} could not be loaded. Please try again.`);
    console.log("Exception when loading ad-hoc mesh for segment", action.segmentId, ":", exc);
    ErrorHandling.notify(exc as any);
  }
}

export function* getMeshExtraInfo(
  layerName: string,
  maybeExtraInfo: AdHocMeshInfo | null | undefined,
): Saga<AdHocMeshInfo> {
  const activeMappingByLayer = yield* select(
    (state) => state.temporaryConfiguration.activeMappingByLayer,
  );
  if (maybeExtraInfo != null) return maybeExtraInfo;
  const mappingInfo = getMappingInfo(activeMappingByLayer, layerName);
  const isMappingActive = mappingInfo.mappingStatus === MappingStatusEnum.ENABLED;
  const mappingName = isMappingActive ? mappingInfo.mappingName : null;
  const mappingType = isMappingActive ? mappingInfo.mappingType : null;
  return {
    mappingName,
    mappingType,
  };
}

function* getInfoForMeshLoading(
  layer: DataLayer,
  meshExtraInfo: AdHocMeshInfo,
): Saga<{
  zoomStep: number;
  magInfo: MagInfo;
}> {
  const magInfo = getMagInfo(layer.mags);
  const preferredZoomStep =
    meshExtraInfo.preferredQuality != null
      ? meshExtraInfo.preferredQuality
      : yield* select(
          (state) => state.temporaryConfiguration.preferredQualityForMeshAdHocComputation,
        );
  const zoomStep = magInfo.getClosestExistingIndex(preferredZoomStep);
  return {
    zoomStep,
    magInfo: magInfo,
  };
}

function* loadAdHocMesh(
  seedPosition: Vector3,
  seedAdditionalCoordinates: AdditionalCoordinate[] | undefined | null,
  segmentId: bigint,
  removeExistingMesh: boolean = false,
  layerName: string,
  maybeExtraInfo?: AdHocMeshInfo,
): Saga<void> {
  const layer = Model.getLayerByName(layerName);

  if (segmentId === 0n) {
    return;
  }

  yield* call([Model, Model.ensureSavedState]);

  const meshExtraInfo = yield* call(getMeshExtraInfo, layer.name, maybeExtraInfo);

  const { zoomStep, magInfo } = yield* call(getInfoForMeshLoading, layer, meshExtraInfo);
  batchCounterPerSegment[segmentId.toString()] = 0;

  // If a REMOVE_MESH action is dispatched and consumed
  // here before loadFullAdHocMesh is finished, the latter saga
  // should be canceled automatically to avoid populating mesh data even though
  // the mesh was removed. This is accomplished by redux-saga's race effect.
  yield* race({
    loadFullAdHocMesh: call(
      loadFullAdHocMesh,
      layer,
      segmentId,
      seedPosition,
      seedAdditionalCoordinates,
      zoomStep,
      meshExtraInfo,
      magInfo,
      removeExistingMesh,
    ),
    cancel: take(
      ((action: Action) =>
        action.type === "REMOVE_MESH" &&
        action.segmentId === segmentId &&
        action.layerName === layer.name) as ActionPattern,
    ),
  });
  removeMeshWithoutVoxels(segmentId, layer.name, seedAdditionalCoordinates);
}

function removeMeshWithoutVoxels(
  segmentId: bigint,
  layerName: string,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
) {
  // If no voxels were added to the scene (e.g. because the segment doesn't have any voxels in this n-dimension),
  // remove it from the store's state as well.
  const { segmentMeshController } = getSceneController();
  if (!segmentMeshController.hasMesh(segmentId, layerName, additionalCoordinates)) {
    Store.dispatch(removeMeshAction(layerName, segmentId));
  }
}

function* getUsePositionsFromSegmentIndex(
  volumeTracing: VolumeTracing | null | undefined,
  dataset: StoreDataset,
  layerName: string,
  maybeTracingId?: string | null,
): Saga<boolean> {
  if (volumeTracing == null) {
    return yield* call(
      hasSegmentIndexInDataStoreCached,
      dataset.dataStore.url,
      dataset.id,
      layerName,
    );
  }
  return (
    volumeTracing?.hasSegmentIndex && !volumeTracing.hasEditableMapping && maybeTracingId != null
  );
}

function* loadFullAdHocMesh(
  layer: DataLayer,
  segmentId: bigint,
  position: Vector3,
  additionalCoordinates: AdditionalCoordinate[] | undefined | null,
  zoomStep: number,
  meshExtraInfo: AdHocMeshInfo,
  magInfo: MagInfo,
  removeExistingMesh: boolean,
): Saga<void> {
  let isInitialRequest = true;
  const { mappingName, mappingType, opacity, isVisible } = meshExtraInfo;
  const clippedPosition = clipPositionToCubeBoundary(position, zoomStep, magInfo);
  yield* put(
    addAdHocMeshAction(
      layer.name,
      segmentId,
      position,
      additionalCoordinates,
      mappingName,
      mappingType,
      opacity,
      isVisible,
    ),
  );
  yield* put(startedLoadingMeshAction(layer.name, segmentId));

  // Limit the number of segments meshed concurrently. Acquiring here (after the
  // loading indicator is shown) means the UI reflects the pending load immediately.
  yield call(acquireMeshWorker);
  try {
    const cubeSize = marchingCubeSizeInTargetMag();
    const dataset = yield* select((state) => state.dataset);
    const mag = magInfo.getMagByIndexOrThrow(zoomStep);

    const volumeTracing = yield* select((state) => getActiveSegmentationTracing(state));
    const annotation = yield* select((state) => state.annotation);
    const visibleSegmentationLayer = yield* select((state) => getVisibleSegmentationLayer(state));
    if (visibleSegmentationLayer == null) {
      throw new Error(
        "Loading the ad-hoc mesh failed because the visible segmentation layer must not be null.",
      );
    }
    // Fetch from datastore if no volumetracing ...
    let forceUsingDataStore = volumeTracing == null || visibleSegmentationLayer.tracingId == null;
    if (meshExtraInfo.useDataStore != null) {
      // ... except if the caller specified whether to use the data store ...
      forceUsingDataStore = meshExtraInfo.useDataStore;
    } else if (volumeTracing?.hasEditableMapping) {
      // ... or if an editable mapping is active.
      forceUsingDataStore = false;
    }

    // Segment stats can only be used for segmentation layers that have a segment index
    // and that don't have editable mappings.
    const usePositionsFromSegmentIndex = yield* call(
      getUsePositionsFromSegmentIndex,
      volumeTracing,
      dataset,
      layer.name,
      visibleSegmentationLayer.tracingId,
    );

    const layerSourceInfo: LayerSourceInfo = {
      dataset,
      annotation,
      tracingId: visibleSegmentationLayer.tracingId,
      segmentationLayerName:
        visibleSegmentationLayer.fallbackLayer ?? visibleSegmentationLayer.name,
      useDataStore: forceUsingDataStore,
    };

    let positionsToRequest = usePositionsFromSegmentIndex
      ? yield* getChunkPositionsFromSegmentIndex(
          layerSourceInfo,
          segmentId,
          cubeSize,
          mag,
          clippedPosition,
          additionalCoordinates,
          mappingName,
          annotation.version,
        )
      : [clippedPosition];

    if (positionsToRequest.length === 0) {
      //if no positions are requested, remove the mesh,
      //so that the old one isn't displayed anymore
      yield* put(removeMeshAction(layer.name, segmentId));
    }
    while (positionsToRequest.length > 0) {
      const currentPosition = positionsToRequest.shift();
      if (currentPosition == null) {
        throw new Error("Satisfy typescript");
      }
      const neighbors = yield* call(
        maybeLoadMeshChunk,
        layer,
        segmentId,
        currentPosition,
        zoomStep,
        meshExtraInfo,
        magInfo,
        isInitialRequest,
        removeExistingMesh && isInitialRequest,
        layerSourceInfo,
        !usePositionsFromSegmentIndex,
      );
      isInitialRequest = false;

      // If we are using the positions from the segment index, the backend will
      // send an empty neighbors array, as it's not necessary to have them.
      if (usePositionsFromSegmentIndex && neighbors.length > 0) {
        throw new Error("Retrieved neighbor positions even though these were not requested.");
      }
      positionsToRequest = positionsToRequest.concat(neighbors);
    }

    yield* put(finishedLoadingMeshAction(layer.name, segmentId));
  } finally {
    yield* call(releaseMeshWorker);
  }
}

function* getChunkPositionsFromSegmentIndex(
  layerSourceInfo: LayerSourceInfo,
  segmentId: bigint,
  cubeSize: Vector3,
  mag: Vector3,
  clippedPosition: Vector3,
  additionalCoordinates: AdditionalCoordinate[] | null | undefined,
  mappingName: string | null | undefined,
  tracingVersion: number,
) {
  const targetMagPositions = yield* call(
    getBucketPositionsForAdHocMesh,
    layerSourceInfo,
    segmentId,
    cubeSize,
    mag,
    additionalCoordinates,
    mappingName,
    tracingVersion,
  );
  const mag1Positions = targetMagPositions.map((pos) => V3.scale3(pos, mag));
  return sortByDistanceTo(mag1Positions, clippedPosition) as Vector3[];
}

function hasMeshChunkExceededThrottleLimit(segmentId: bigint): boolean {
  return batchCounterPerSegment[segmentId.toString()] > MESH_CHUNK_THROTTLE_LIMIT;
}

function* maybeLoadMeshChunk(
  layer: DataLayer,
  segmentId: bigint,
  clippedPosition: Vector3,
  zoomStep: number,
  meshExtraInfo: AdHocMeshInfo,
  magInfo: MagInfo,
  isInitialRequest: boolean,
  removeExistingMesh: boolean,
  layerSourceInfo: LayerSourceInfo,
  findNeighbors: boolean,
): Saga<Vector3[]> {
  const additionalCoordinates = yield* select((state) => state.flycam.additionalCoordinates);
  const annotationVersion = yield* select((state) => state.annotation.version);
  const threeDMap = getOrAddMapForSegment(layer.name, segmentId, additionalCoordinates);
  const mag = magInfo.getMagByIndexOrThrow(zoomStep);

  /*
  Both cube position and cubeSize are padded. This is to achieve two effects:
  (1) An overlap of 1vx (target mag) is added between cubes to fill visible gaps in the
      mesh chunk grid.
  (2) Cubes directly at dataset layer borders should actually extend by 1vx (target mag)
     *beyond* the layer border so that marchingCubes can add closing surfaces for segments
     that touch the layer borders.
  To achieve both, all positions are moved by 1vx towards topleft and all sizes increased by 1.
  Additionally, cubes that touch a lower layer border are increased by 1 again in that direction.
  Note that this process can result in negative positions at the topleft layer border.
  This is expected and the backend will handle it, adding the closing surface.
  */
  const paddedPosition = V3.sub(clippedPosition, mag);
  const paddedCubeSize = getPaddedCubeSizeInTargetMag(paddedPosition, mag, layer);

  if (threeDMap.get(paddedPosition)) {
    return [];
  }

  if (hasMeshChunkExceededThrottleLimit(segmentId)) {
    yield* call(sleep, MESH_CHUNK_THROTTLE_DELAY);
  }

  batchCounterPerSegment[segmentId.toString()]++;
  threeDMap.set(paddedPosition, true);
  const scaleFactor = yield* select((state) => state.dataset.dataSource.scale.factor);

  if (isInitialRequest) {
    sendAnalyticsEvent("request_isosurface", {
      mode: layerSourceInfo.useDataStore ? "view" : "annotation",
    });
  }

  let retryCount = 0;

  const { segmentMeshController } = getSceneController();

  while (retryCount < MAX_RETRY_COUNT) {
    try {
      const { buffer: responseBuffer, neighbors } = yield* call(
        {
          context: null,
          fn: computeAdHocMesh,
        },
        layerSourceInfo,
        {
          positionWithPadding: paddedPosition,
          additionalCoordinates: additionalCoordinates || undefined,
          mag,
          segmentId,
          cubeSize: paddedCubeSize,
          scaleFactor,
          findNeighbors,
          annotationVersion,
          ...meshExtraInfo,
        },
      );
      const vertices = new Float32Array(responseBuffer);

      if (removeExistingMesh) {
        segmentMeshController.removeMeshById(segmentId, layer.name);
      }

      // We await addMeshFromVerticesAsync here, because the mesh saga will remove
      // an ad-hoc loaded mesh immediately if it was "empty". Since the check is
      // done by looking at the scene, we await the population of the scene.
      // Theoretically, this could be built differently so that other ad-hoc chunks
      // can be loaded in parallel to addMeshFromVerticesAsync. However, it's unclear
      // how big the bottleneck really is.
      yield* call(
        { fn: segmentMeshController.addMeshFromVerticesAsync, context: segmentMeshController },
        vertices,
        segmentId,
        layer.name,
        meshExtraInfo.opacity,
        additionalCoordinates,
      );
      return neighbors.map((neighbor) =>
        getNeighborPosition(clippedPosition, neighbor, zoomStep, magInfo),
      );
    } catch (exception) {
      retryCount++;
      ErrorHandling.notify(exception as Error);
      console.warn("Retrying mesh generation due to", exception);
      yield* call(sleep, RETRY_WAIT_TIME * 2 ** retryCount);
    }
  }

  return [];
}

function getPaddedCubeSizeInTargetMag(
  paddedPosition: Vector3,
  mag: Vector3,
  layer: DataLayer,
): Vector3 {
  let cubeSize = marchingCubeSizeInTargetMag();

  // Always increase cubeSize by 1,1,1 to fill grid gaps
  cubeSize = V3.add(cubeSize, [1, 1, 1]);

  // If a cube precisely touches a lower layer border, increase its size in that direction
  // by 1 again, to provide a closing surface on that layer border.
  // Note that the paddedPosition already takes care of the upper layer borders
  const cubeBottomRight = V3.add(paddedPosition, V3.scale3(cubeSize, mag));
  const layerBottomRight = layer.cube.boundingBox.max;
  for (let dimension = 0; dimension < 3; dimension++) {
    if (cubeBottomRight[dimension] === layerBottomRight[dimension]) {
      cubeSize[dimension] += 1;
    }
  }
  return cubeSize;
}

function* markEditedCellAsDirty(): Saga<void> {
  const volumeTracing = yield* select((state) => getActiveSegmentationTracing(state));

  if (volumeTracing != null && volumeTracing.fallbackLayer == null) {
    const activeCellId = volumeTracing.activeCellId;
    modifiedCells.add(activeCellId);
  }
}

// This function is only called when the front-end api.data.refreshMeshes is used.
function* refreshMeshes(): Saga<void> {
  yield* put(saveNowAction());
  // We reload all cells that got modified till the start of reloading.
  // By that we avoid to remove cells that got annotated during reloading from the modifiedCells set.
  const currentlyModifiedCells = new Set(modifiedCells);
  modifiedCells.clear();

  const additionalCoordinates = yield* select((state) => state.flycam.additionalCoordinates);
  const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);
  const segmentationLayer = Model.getVisibleSegmentationLayer();

  if (!segmentationLayer) {
    return;
  }

  adhocMeshesMapByLayer[additionalCoordKey][segmentationLayer.name] =
    adhocMeshesMapByLayer[additionalCoordKey][segmentationLayer.name] || new Map();
  const meshesMapForLayer = adhocMeshesMapByLayer[additionalCoordKey][segmentationLayer.name];

  for (const [segmentId, threeDMap] of Array.from(meshesMapForLayer.entries())) {
    if (!currentlyModifiedCells.has(segmentId)) {
      continue;
    }

    const meshInfo = yield* select((state) =>
      getMeshInfoForSegment(state, additionalCoordinates, segmentationLayer.name, segmentId),
    );

    yield* call(
      refreshMeshWithMap,
      segmentId,
      threeDMap,
      segmentationLayer.name,
      additionalCoordinates,
      meshInfo?.opacity,
    );
  }
}

function* refreshMesh(action: RefreshMeshAction): Saga<void> {
  const additionalCoordinates = yield* select((state) => state.flycam.additionalCoordinates);
  const additionalCoordKey = getAdditionalCoordinatesAsString(additionalCoordinates);

  const { segmentId, layerName } = action;

  const meshInfo = yield* select((state) =>
    getMeshInfoForSegment(state, additionalCoordinates, layerName, segmentId),
  );

  if (meshInfo == null) {
    throw new Error(
      `Mesh refreshing failed due to lack of mesh info for segment ${segmentId} in store.`,
    );
  }

  if (meshInfo.isPrecomputed) {
    yield* put(removeMeshAction(layerName, meshInfo.segmentId));
    yield* put(
      loadPrecomputedMeshAction(
        meshInfo.segmentId,
        meshInfo.seedPosition,
        meshInfo.seedAdditionalCoordinates,
        meshInfo.meshFileName,
        meshInfo.opacity,
        meshInfo.isVisible,
        layerName,
      ),
    );
  } else {
    if (adhocMeshesMapByLayer[additionalCoordKey] == null) return;
    const threeDMap = adhocMeshesMapByLayer[additionalCoordKey][action.layerName].get(segmentId);
    if (threeDMap == null) {
      return;
    }
    yield* call(
      refreshMeshWithMap,
      segmentId,
      threeDMap,
      layerName,
      additionalCoordinates,
      meshInfo.opacity,
    );
  }
}

function* refreshMeshWithMap(
  segmentId: bigint,
  threeDMap: ThreeDMap<boolean>,
  layerName: string,
  additionalCoordinates: AdditionalCoordinate[] | null,
  opacity: number | undefined,
): Saga<void> {
  const meshInfo = yield* select((state) =>
    getMeshInfoForSegment(state, additionalCoordinates, layerName, segmentId),
  );
  if (meshInfo == null) {
    throw new Error(
      `Mesh refreshing failed due to lack of mesh info for segment ${segmentId} in store.`,
    );
  }
  yield* call(
    [ErrorHandling, ErrorHandling.assert],
    !meshInfo.isPrecomputed,
    "_refreshMeshWithMap was called for a precomputed mesh.",
  );
  if (meshInfo.isPrecomputed) return;
  const { mappingName, mappingType } = meshInfo;
  const meshPositions = threeDMap.entries().filter(([value, _position]) => value);

  if (meshPositions.length === 0) {
    return;
  }

  // Remove mesh from cache.
  yield* call(removeMesh, removeMeshAction(layerName, segmentId), false);
  // The mesh should only be removed once after re-fetching the mesh first position.
  let shouldBeRemoved = true;

  for (const [, position] of meshPositions) {
    // Reload the mesh at the given position if it isn't already loaded there.
    // This is done to ensure that every voxel of the mesh is reloaded.
    yield* call(
      loadAdHocMesh,
      position,
      additionalCoordinates,
      segmentId,
      shouldBeRemoved,
      layerName,
      {
        mappingName,
        mappingType,
        opacity,
      },
    );
    shouldBeRemoved = false;
  }
}

export default function* adHocMeshSaga(): Saga<void> {
  // Buffer actions since they might be dispatched before WK_INITIALIZED
  const loadAdHocMeshActionChannel = yield* actionChannel("LOAD_AD_HOC_MESH_ACTION");

  yield* call(ensureSceneControllerInitialized);
  yield* call(ensureWkInitialized);
  yield* takeEvery(loadAdHocMeshActionChannel, loadAdHocMeshFromAction);
  yield* takeEvery("REMOVE_MESH", removeMesh);
  yield* takeEvery("REFRESH_MESHES", refreshMeshes);
  yield* takeEvery("REFRESH_MESH", refreshMesh);
  yield* takeEvery(["START_EDITING", "COPY_SEGMENTATION_LAYER"], markEditedCellAsDirty);
}
